<template>
  <div>
    <b-loading is-full-page v-model="isLoading"></b-loading>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching recipe!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>
    <Keypress key-event="keyup" :modifiers="['ctrlKey']" :key-code="83" :preventDefault="true" @success="save" />

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Recipes"
      :currentTitle="title || 'Loading...'"
      :backRoute="{ name: 'RecipeListView' }"
    >
      <template v-if="showTopBarMeta && isDesktop" #subtitle>
        <div class="topbar-meta">
          <span class="topbar-meta-item"><b-icon icon="clock-outline" size="is-small" /> {{ totalTime }} mins</span>
          <span class="topbar-meta-item"><b-icon icon="account-group-outline" size="is-small" /> {{ recipe.servings }}</span>
          <span class="topbar-meta-item"><b-icon icon="format-list-bulleted" size="is-small" /> {{ recipe.ingredients.length }}</span>
        </div>
      </template>
      <FeedsStatusBadge :status="recipe.status" />
      <b-button outlined type="is-primary" size="is-small" icon-left="check" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" @click="save">Save</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ title }}</h1>
          <div ref="metaBar" class="recipe-meta-bar">
            <span class="meta-pill"><b-icon icon="clock-outline" size="is-small" /> {{ totalTime }} mins</span>
            <span class="meta-pill"><b-icon icon="account-group-outline" size="is-small" /> Serves {{ recipe.servings }}</span>
            <span class="meta-pill"><b-icon icon="format-list-bulleted" size="is-small" /> {{ recipe.ingredients.length }} ingredients</span>
          </div>
        </div>
      </div>

      <!-- Top Section: General Info + Images -->
      <div class="top-grid">
        <!-- Left Column: General & Search -->
        <div class="form-column">
          <!-- General Info Card -->
          <FeedsCard title="General Information">

            <!-- Variant Info -->
            <div v-if="recipe.is_variant" class="variant-info mb-4">
              <div class="form-group">
                <label class="form-label">Variant of</label>
                <div class="variant-controls">
                  <b-input expanded disabled :value="recipe.base_recipe.title" />
                  <b-button type="is-warning" icon-left="swap-horizontal" @click="convertToBaseRecipe()">Convert to Base</b-button>
                  <b-button icon-right="arrow-right" @click="viewBaseRecipe()">View Recipe</b-button>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Internal Name</label>
                <b-input v-model="recipe.name" @input="saveDebounced" />
                <p class="form-hint">This is an internal name and will not be seen by the end user</p>
              </div>
            </div>

            <!-- Meal Plans Toggle -->
            <div class="form-group">
              <b-button type="is-light" icon-left="calendar" expanded @click="toggleMealplans">
                {{ isViewMealplans ? 'Hide Meal Plans' : 'Show Meal Plans' }}
              </b-button>
            </div>

            <!-- Mealplans Table (if visible) -->
            <div v-if="isViewMealplans && mealplans != null" class="mealplans-section mb-4">
              <label class="form-label">Used in Meal Plans</label>
              <b-table :data="mealplans" :striped="true" :hoverable="true">
                <b-table-column field="title" label="Title" v-slot="props">
                  <p class="has-text-weight-bold">{{ props.row.title }}</p>
                </b-table-column>
                <b-table-column field="status" width=100 label="Status" centered v-slot="props">
                  <FeedsStatusBadge :status="props.row.status" />
                </b-table-column>
                <b-table-column field="date" width=120 label="Date" centered v-slot="props">
                  <span class="date-badge">{{ formatDate(props.row.date) }}</span>
                </b-table-column>
                <b-table-column field="-" width=80 centered v-slot="props">
                  <b-button size="is-small" outlined icon-right="chevron-right" @click="viewMealPlan(props.row)">View</b-button>
                </b-table-column>
              </b-table>
            </div>

            <!-- Form Fields -->
            <div class="form-group">
              <label class="form-label">Recipe Title</label>
              <b-input v-model="recipe.title" @input="saveDebounced" />
            </div>

            <div class="form-group">
              <label class="form-label">Description</label>
              <editor v-model="recipe.description" @input="saveDebounced"/>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Prep Time</label>
                <b-input v-model="recipe.prepTime" placeholder="e.g. 20 mins" @input="saveDebounced"/>
              </div>
              <div class="form-group">
                <label class="form-label">Cook Time</label>
                <b-input v-model="recipe.cookTime" placeholder="e.g. 50 mins" @input="saveDebounced"/>
              </div>
              <div class="form-group">
                <label class="form-label">Serves</label>
                <b-numberinput v-model="recipe.servings" placeholder="e.g. 2" type="is-light" controls-position="compact" :min="2" :max="8"></b-numberinput>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Meal Type</label>
                <b-select v-model="recipe.meal" placeholder="Select a meal" expanded @input="save">
                  <option>lunch</option>
                  <option>dinner</option>
                  <option>brunch</option>
                  <option>lunch or dinner</option>
                  <option>lunch, dinner or brunch</option>
                </b-select>
              </div>
              <div class="form-group">
                <label class="form-label">Status</label>
                <b-select v-model="recipe.status" placeholder="Status" expanded @input="onStatusChange">
                  <option>draft</option>
                  <option>test</option>
                  <option>fix</option>
                  <option>live</option>
                </b-select>
              </div>
              <div class="form-group">
                <label class="form-label">Free Recipe</label>
                <div class="toggle-group">
                  <div class="toggle" :class="{ active: recipe.is_free }" @click="toggleFree()"></div>
                  <span class="toggle-label">{{ recipe.is_free ? 'Yes' : 'No' }}</span>
                </div>
              </div>
            </div>

            <div class="form-row" style="grid-template-columns: 1fr 2fr;">
              <div class="form-group">
                <label class="form-label">Published Date</label>
                <b-datepicker
                  v-model="publishedDate"
                  trap-focus
                  placeholder="Not published"
                  icon="calendar-today"
                  :icon-right="recipe.published_at ? 'close-circle' : ''"
                  icon-right-clickable
                  @icon-right-click="clearPublishedDate"
                  @input="save"
                />
              </div>
            </div>

            <!-- Share Link -->
            <ShareLink
              v-model="recipe.share_link"
              :selectedPath="`recipe-${id}`"
              :destinationUrl="`https://feedsfeedsfeeds.com/?recipe_id=${id}`"
              :linkName="`Recipe: ${recipe.title}`"
              @input="save"
            />
          </FeedsCard>

          <!-- Tags & Search Card -->
          <FeedsCard title="Tags & Search">
            <EditTags
              v-model="recipe.tags"
              entityType="recipes"
              @input="save"
            />
            <EditSeasonalAvailability
              v-model="recipe.seasonal_availability"
              @input="save"
            />
            <EditSearchSynonyms
              v-model="recipe.search_synonyms"
              @input="save"
            />
          </FeedsCard>
        </div>

        <!-- Right Column: Images -->
        <div class="preview-column">
          <FeedsCard title="Images">
            <template #header-actions>
              <b-button size="is-small" type="is-primary" icon-left="plus" @click="$refs.editImages.openAddImage()">Add Image</b-button>
            </template>
            <EditImages
              ref="editImages"
              v-model="recipe.images"
              @input="save"
            />
          </FeedsCard>
        </div>
      </div>

      
       <div class="recipe-content-grid -mt-5">
        <!-- Tips Section -->
        <FeedsCard title="Before You Begin..." class="mt-5">
          <EditTips
            v-model="recipe.tips"
            title=""
            @input="save"
          />
        </FeedsCard>

        <!-- Notes Section -->
        <FeedsCard title="Internal Notes" class="mt-5">
          <p class="form-hint mb-3">These notes are for internal use only and will not be visible to users.</p>
          <editor v-model="recipe.notes" @input="saveDebounced"/>
        </FeedsCard>
      </div>

      <!-- Ingredients & Method Side by Side -->
      <div class="recipe-content-grid mt-5">
        <FeedsCard title="Method" :compact="true">
          <EditRecipeSteps
            v-model="recipe.steps"
            :ingredients="recipe.ingredients"
            @input="save"
          />
        </FeedsCard>
        <FeedsCard title="Ingredients" :compact="true">
          <EditRecipeIngredients
            v-model="recipe.ingredients"
            @ingredient-removed="ingredientRemoved($event)"
            @input="save"
          />
        </FeedsCard>
      </div>


      <!-- Preview Section -->
      <FeedsCard title="Recipe Preview" class="mt-5">
        <template #header-actions>
          <b-button size="is-small" type="is-primary" icon-left="content-copy" @click="copyForBlog">
            Copy for Blog
          </b-button>
        </template>
        <div class="preview-content">
          <h2 class="preview-title">{{ recipe.title }}</h2>

          <b-carousel v-if="recipe.images && recipe.images.length > 0" :autoplay="true" class="preview-carousel">
            <b-carousel-item v-for="(image, i) in recipe.images" :key="i">
              <b-image class="image" :src="image.src"></b-image>
            </b-carousel-item>
          </b-carousel>

          <div class="preview-description" v-html="recipe.description"></div>

          <div class="preview-meta">
            <span class="meta-item"><b-icon icon="knife" size="is-small" /> {{ recipe.prepTime }}</span>
            <span class="meta-item"><b-icon icon="pot-steam" size="is-small" /> {{ recipe.cookTime }}</span>
            <span class="meta-item"><b-icon icon="account-group" size="is-small" /> Serves {{ recipe.servings }}</span>
          </div>

          <div class="preview-sections">
            <IngredientList :ingredients="recipe.ingredients" />
            <StepList :steps="recipe.steps" :ingredients="recipe.ingredients" />
          </div>
        </div>
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import EditTips from "@/components/EditTips"
import EditImages from "@/components/EditImages"
import EditRecipeIngredients from "@/components/EditRecipeIngredients"
import EditRecipeSteps from "@/components/EditRecipeSteps"
import IngredientList from "@/components/IngredientList"
import StepList from "@/components/StepList"
import Editor from "@/components/BaseEditor"
import EditTags from "@/components/EditTags"
import EditSearchSynonyms from "@/components/EditSearchSynonyms"
import EditSeasonalAvailability from "@/components/EditSeasonalAvailability"
import FeedsCard from "@/components/common/FeedsCard"
import FeedsStatusBadge from "@/components/common/FeedsStatusBadge"
import TopBar from "@/components/layout/TopBar"
import ShareLink from "@/components/common/ShareLink"
import db from "@/firebase/db"
import DateFormatter from "@/formatters/DateFormatter"

export default {
  name: 'RecipeView',
  components: {
    EditTips,
    EditImages,
    EditRecipeIngredients,
    EditRecipeSteps,
    IngredientList,
    StepList,
    Editor,
    EditTags,
    EditSearchSynonyms,
    EditSeasonalAvailability,
    FeedsCard,
    FeedsStatusBadge,
    TopBar,
    ShareLink,
    Keypress: () => import('vue-keypress')
  },
  props: ['id'],
  data() {
    return {
      recipe: {},
      mealplans: null,
      isViewMealplans: false,
      isLoading: true,
      isError: false,
      showTopBarMeta: false,
      isDesktop: window.innerWidth > 768
    };
  },
  mounted() {
    this._onScroll = () => {
      if (this.$refs.metaBar) {
        const rect = this.$refs.metaBar.getBoundingClientRect()
        this.showTopBarMeta = rect.bottom < 0
      }
    }
    window.addEventListener('scroll', this._onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll)
  },
  computed: {
    title() {
      if (this.recipe.name != null && this.recipe.name != ""){
        return `${this.recipe.title} (${this.recipe.name})`
      }
      return this.recipe.title ?? "New Recipe"
    },
    newStepIndex() {
      return this.recipe.steps?.length + 1 ?? 1
    },
    publishedDate: {
      get() {
        return this.recipe.published_at ? new Date(this.recipe.published_at) : null;
      },
      set(val) {
        this.recipe.published_at = val.getTime();
      }
    },
    totalTime() {
      const firstNum = (str) => { const m = str && str.match(/\d+/); return m ? parseInt(m[0]) : 0 }
      return firstNum(this.recipe.prepTime) + firstNum(this.recipe.cookTime)
    }
  },
  created: function () {
    this.$watch(
      () => this.$route.params,
      () => {
        this.fetchRecipe()
      },
      { immediate: true }
    )
  },
  methods: {
    fetchRecipe() {
      db.collection('recipes')
        .doc(this.id)
        .get()
        .then(snapshot => {
          if (snapshot.exists) {
            this.recipe = snapshot.data();
          }else {
            this.isError = true;
          }
          this.isLoading = false;

          let message = `You're editing a <b>${this.recipe.is_variant ? "Variant":"BASE"}</b> recipe. It's status is <b>${this.recipe.status.toUpperCase()}</b>!`

          this.$buefy.toast.open({
                      message: message,
                      type: this.recipe.status == "live" ? 'is-danger':'is-warning',
                      position: 'is-top',
                      actionText: 'OK',
                      duration: 3000,
                  });
        })
        .catch((error) => {
          this.isError = true;
          this.$buefy.notification.open({type: 'is-danger', message: `Error fetching recipe: ${error}`})
        });
    },
    clearPublishedDate() {
      this.recipe.published_at = null;
      this.save();
    },
    onStatusChange() {
      if (this.recipe.status === 'live' && !this.recipe.published_at) {
        this.$set(this.recipe, 'published_at', Date.now());
      }
      this.save();
    },
    saveDebounced: debounce(function () {
        this.save();
      }, 3000),
    saveAndClose(){
      this.save()
        .then(() => {
          this.$router.go(-1);
        })
    },
    async save () {
      const badIngredients = (this.recipe.ingredients || []).filter(
        i => i.aisle == null || i.ingredient_id == null
      );
      if (badIngredients.length > 0) {
        const names = badIngredients.map(i => i.name || '(unnamed)').join(', ');
        this.$buefy.toast.open({
          type: 'is-danger',
          message: `Cannot save: ${badIngredients.length} ingredient(s) must be selected from the dropdown — ${names}`,
          duration: 5000,
          position: 'is-top'
        });
        return Promise.resolve();
      }
      return db.collection('recipes')
        .doc(this.id)
        .set(this.recipe)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Recipe successfully updated!`});
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving recipe: ${error}`});
        });
    },
    convertToBaseRecipe() {
      this.$buefy.dialog.confirm({
          title: 'Converting to a base recipe',
          message: `Are you sure you want to convert <b><i>"${this.recipe.title} (${this.recipe.name})"</i></b> to be a base? <br /><br />This action cannot be undone and some data can be lost.`,
          confirmText: 'Convert to base recipe',
          type: 'is-warning',
          hasIcon: true,
          onConfirm: () => {
            this.recipe.is_variant = false;
            this.recipe.name = "";
            this.save();
          }
        })
    },
    viewBaseRecipe() {
      this.$router.push({ name: 'RecipeView', params: { id: this.recipe.base_recipe.id } });
    },
    async toggleMealplans () {
      this.isViewMealplans = !this.isViewMealplans;

      if (this.mealplans != null){
        return;
      }
      let recipeIds = await this.getAllRelatedRecipeIds();
      this.setMealPlans(recipeIds);
    },
    async getAllRelatedRecipeIds(){
      return new Promise((resolve) => {
        let ids = [this.id];
        if (this.recipe.is_variant){
          if (this.recipe.base_recipe.id != null) { ids.push(this.recipe.base_recipe.id); }
          db.collection('recipes')
            .doc(this.recipe.base_recipe.id)
            .get()
            .then(snapshot => {
              if (snapshot.exists) {
                let baseRecipe = snapshot.data();
                if (baseRecipe.recipe_variants != null) { ids = ids.concat(Object.keys(baseRecipe.recipe_variants)); }
                ids = [...new Set(ids)];
                resolve(ids);
              }
            })
        }else{
          if (this.recipe.recipe_variants != null) { ids = ids.concat(Object.keys(this.recipe.recipe_variants)); }
          ids = [...new Set(ids)];
          resolve(ids);
        }
      })
    },
    setMealPlans(recipeIds) {
      if (this.mealplans == null){
        for (let i = 0; i < recipeIds.length; i++){
          let id = recipeIds[i];
          db.collection("recipe_mealplans").doc(id)
          .get()
          .then(snapshot => {
            if (snapshot.data() == null){
              this.$buefy.toast.open({type: 'is-warning', message: `Recipe is not used in any meal plans`});
              return;
            }

            let mealPlanKeys = Object.keys(snapshot.data());
            mealPlanKeys.forEach(id => {
              db.collection("meal_plans").doc(id)
                .get()
                .then(snapshot => {
                  if (this.mealplans == null) {
                    this.mealplans = [];
                  }
                  let mealplan = {
                    id: id,
                    ...snapshot.data()
                  };
                  this.mealplans.push(mealplan);
                  this.mealplans.sort((a, b) => b.date - a.date);
                })
            });
          })
        }
      }
    },
    formatDate(dateTime) {
      return DateFormatter.displayDate(dateTime);
    },
    viewMealPlan(plan) {
      this.$router.push({ name: 'MealPlanView', params: { id: plan.id } })
    },
    ingredientRemoved(ingredient) {
      this.recipe.steps.filter(step => step.ingredients.length>0).forEach((step) => {
        const index = step.ingredients.map((i) => { return i.id } ).indexOf(ingredient.id);
        if (index >= 0) this.$delete(step.ingredients, index);
      });
    },
    toggleFree() {
      this.recipe.is_free = !this.recipe.is_free;
      this.saveDebounced();
    },
    formatAmount(num) {
      const fractions = {
        0.125: '⅛', 0.25: '¼', 0.333: '⅓', 0.5: '½',
        0.667: '⅔', 0.75: '¾'
      }
      const whole = Math.floor(num)
      const decimal = parseFloat((num - whole).toFixed(3))
      const frac = fractions[decimal]
      if (frac) {
        return whole > 0 ? `${whole}${frac}` : frac
      }
      return String(num)
    },
    copyForBlog() {
      const r = this.recipe
      const lines = []

      // Title
      lines.push(`<h1>${r.title}</h1>`)

      // Description
      if (r.description) {
        lines.push(r.description)
      }

      // Meta
      const metaParts = []
      if (r.prepTime) metaParts.push(`Prep: ${r.prepTime}`)
      if (r.cookTime) metaParts.push(`Cook: ${r.cookTime}`)
      if (r.servings) metaParts.push(`Serves: ${r.servings}`)
      if (metaParts.length) {
        lines.push(`<p><strong>${metaParts.join(' | ')}</strong></p>`)
      }

      // Ingredients
      if (r.ingredients && r.ingredients.length) {
        lines.push(`<h2>Ingredients</h2>`)
        lines.push('<ul>')
        r.ingredients.forEach(ing => {
          const optional = ing.optional ? '(Optional) ' : ''
          const amount = ing.amount ? `${this.formatAmount(ing.amount)} ` : ''
          const unit = ing.unit ? `${ing.unit} ` : ''
          const name = ing.name || ''
          const prep = ing.prep ? ` (${ing.prep})` : ''
          lines.push(`<li>${optional}${amount}${unit}<strong>${name.toLowerCase()}</strong>${prep}`)
          if (ing.tips && ing.tips.length) {
            lines.push('<ul>')
            ing.tips.forEach(tip => {
              if (tip.value) lines.push(`<li><em>${tip.value.replace(/<\/?p>/g, '')}</em></li>`)
            })
            lines.push('</ul>')
          }
          lines.push('</li>')
        })
        lines.push('</ul>')
      }

      // Steps with inline ingredients and tips
      if (r.steps && r.steps.length) {
        lines.push(`<h2>Method</h2>`)
        r.steps.forEach((step, index) => {
          const sentence = step.sentence.replace(/<\/?p>/g, '')
          lines.push(`<p><strong>Step ${index + 1}.</strong> ${sentence}</p>`)
          const hasIngredients = step.ingredients && step.ingredients.length
          const hasTips = step.tips && step.tips.length
          if (hasIngredients || hasTips) {
            lines.push('<ul>')
            if (hasIngredients && r.ingredients) {
              step.ingredients.forEach(stepIng => {
                const ing = r.ingredients.find(i => i.id === stepIng.id)
                if (ing) {
                  const optional = ing.optional ? '(Optional) ' : ''
                  const ratio = stepIng.ratio || 1
                  const amount = ing.amount ? `${this.formatAmount(parseFloat((ing.amount * ratio).toFixed(4)))} ` : ''
                  const unit = ing.unit ? `${ing.unit} ` : ''
                  const name = ing.name ? `<strong>${ing.name.toLowerCase()}</strong>` : ''
                  const prep = ing.prep ? ` (${ing.prep})` : ''
                  lines.push(`<li>${optional}${amount}${unit}${name}${prep}</li>`)
                }
              })
            }
            if (hasTips) {
              step.tips.forEach(tip => {
                if (tip.value) lines.push(`<li><em>${tip.value.replace(/<\/?p>/g, '')}</em></li>`)
              })
            }
            lines.push('</ul>')
          }
        })
      }

      const html = lines.join('\n')

      // Copy as both HTML and plain text for rich paste support
      const blob = new Blob([html], { type: 'text/html' })
      const plainBlob = new Blob([html], { type: 'text/plain' })
      const item = new ClipboardItem({
        'text/html': blob,
        'text/plain': plainBlob
      })
      navigator.clipboard.write([item]).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Recipe copied for blog!' })
      }).catch(() => {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = html
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.$buefy.toast.open({ type: 'is-success', message: 'Recipe HTML copied!' })
      })
    }
  }
}
</script>

<style scoped>
.recipe-meta-bar {
  display: flex;
  gap: 10px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.topbar-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--bg-tertiary, #f5f5f5);
  padding: 4px 10px;
  border-radius: var(--radius-full, 20px);
}

.variant-info {
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
  margin-bottom: 20px;
}

.variant-controls {
  display: flex;
  gap: 8px;
}

.mealplans-section {
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
}

.date-badge {
  display: inline-block;
  padding: 4px 10px;
  background: var(--feeds-green-light);
  color: var(--feeds-green);
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 500;
}

/* Preview Section Styles */
.preview-content {
  max-width: 800px;
}

.preview-title {
  font-family: 'Nunito', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 20px;
}

.preview-carousel {
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: 20px;
}

.preview-description {
  font-size: 16px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.preview-meta {
  display: flex;
  gap: 24px;
  padding: 16px 0;
  border-top: 1px solid var(--border-light);
  border-bottom: 1px solid var(--border-light);
  margin-bottom: 24px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-weight: 500;
}

.preview-sections {
  margin-top: 24px;
}

/* Toggle Switch - matching mockup */
.toggle-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.toggle {
  width: 48px;
  height: 26px;
  background: var(--border-medium);
  border-radius: 13px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
}

.toggle.active {
  background: var(--feeds-green);
}

.toggle::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.15);
}

.toggle.active::after {
  transform: translateX(22px);
}

.toggle-label {
  font-size: 14px;
  color: var(--text-primary);
}

@media (max-width: 768px) {
  .variant-controls {
    flex-direction: column;
  }

  .preview-meta {
    flex-wrap: wrap;
    gap: 12px;
  }
}
</style>
